import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { Shield, Eye, EyeOff, Lock, User, Activity, Globe, CheckCircle2, ShieldCheck, Zap } from 'lucide-react'
import toast from 'react-hot-toast'

export default function LoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const login = useAuthStore((s) => s.login)
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await login(username, password)
      navigate('/dashboard')
    } catch (err) {
      const msg = err.response?.data?.detail || 'Неверный идентификатор или ключ'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB] flex flex-col lg:flex-row font-sans selection:bg-indigo-600 selection:text-white">

      {/* ЛЕВАЯ ПАНЕЛЬ: АВТОРИЗАЦИЯ (45%) */}
      <div className="w-full lg:w-[45%] flex flex-col bg-white relative z-20 shadow-[20px_0_50px_rgba(0,0,0,0.02)]">
        <div className="flex-1 flex flex-col justify-center px-10 sm:px-20 xl:px-32 py-20">

          {/* Logo & Status */}
          <div className="flex items-center justify-between mb-10 sm:mb-20">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-indigo-600 rounded-[14px] flex items-center justify-center shadow-lg shadow-indigo-100">
                <ShieldCheck className="text-white w-6 h-6" />
              </div>
              <div>
                <h1 className="text-lg font-black tracking-tight text-slate-900 leading-none">SENTINEL</h1>
                <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400 mt-1">Access Control</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-100">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest">v{__APP_VERSION__} Live</span>
            </div>
          </div>

          {/* Form Header */}
          <div className="mb-8 sm:mb-12">
            <h2 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter leading-[0.9]">
              Вход в <br />
              <span className="text-indigo-600">Систему.</span>
            </h2>
            <p className="text-slate-400 font-medium text-sm mt-6 max-w-xs leading-relaxed">
              Авторизуйтесь для управления терминалами Hikvision и мониторинга посещаемости.
            </p>
          </div>

          {/* Authentication Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="group">
              <div className="relative border-b-2 border-slate-100 transition-colors focus-within:border-indigo-600">
                <span className="absolute left-0 top-4 text-slate-400">
                  <User size={18} strokeWidth={2.5} />
                </span>
                <input
                  type="text"
                  className="w-full bg-transparent pl-8 pr-4 py-4 text-sm font-bold text-slate-900 outline-none placeholder:text-slate-300"
                  placeholder="Идентификатор пользователя"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="group">
              <div className="relative border-b-2 border-slate-100 transition-colors focus-within:border-indigo-600">
                <span className="absolute left-0 top-4 text-slate-400">
                  <Lock size={18} strokeWidth={2.5} />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="w-full bg-transparent pl-8 pr-12 py-4 text-sm font-bold text-slate-900 outline-none placeholder:text-slate-300"
                  placeholder="Ключ доступа"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="absolute right-0 top-4 text-slate-400"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="pt-10">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 text-white font-black uppercase tracking-[0.25em] text-[11px] py-5 rounded-2xl shadow-2xl shadow-slate-300 flex items-center justify-center gap-3 active:scale-95 transition-transform"
              >
                {loading ? "Аутентификация..." : "Получить доступ"}
              </button>
            </div>
          </form>

          {/* Footer Info */}
          <footer className="mt-10 sm:mt-20">
            <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest leading-relaxed">
              © 2024 University Security Infrastructure <br />
              Global Network Control Protocol
            </p>
          </footer>
        </div>
      </div>

      {/* ПРАВАЯ ПАНЕЛЬ: ТЕХНОЛОГИЧЕСКИЙ СТЕК (55%) */}
      <div className="hidden lg:flex lg:w-[55%] bg-[#0F1115] relative items-center justify-center overflow-hidden p-16">

        {/* Абстрактные геометрические формы */}
        <div className="absolute top-0 right-0 w-[80%] h-[80%] bg-indigo-600/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[50%] h-[50%] bg-blue-600/5 rounded-full blur-[100px]" />

        <div className="relative w-full max-w-2xl space-y-6">

          {/* Layer 1: System Health */}
          <div className="bg-white/[0.03] border border-white/5 p-8 rounded-[32px] backdrop-blur-md">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/10 rounded-xl"><CheckCircle2 className="text-emerald-500" size={20} /></div>
                <span className="text-xs font-black text-white uppercase tracking-widest">System Integrity</span>
              </div>
              <span className="text-[10px] font-mono font-bold text-slate-500 tracking-tighter">NODE_OS_UPTIME: 100.0%</span>
            </div>
            <div className="grid grid-cols-3 gap-8">
              {[
                { label: 'Latency', val: '14ms', color: 'text-indigo-400' },
                { label: 'Security', val: 'High', color: 'text-emerald-400' },
                { label: 'Auth', val: '2FA', color: 'text-blue-400' }
              ].map((item, i) => (
                <div key={i}>
                  <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">{item.label}</p>
                  <p className={`text-xl font-black ${item.color} tracking-tight`}>{item.val}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Layer 2: Visual Monitor */}
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-indigo-600 rounded-[32px] p-8 flex flex-col justify-between h-64 shadow-2xl shadow-indigo-900/20">
              <Activity className="text-white/40" size={32} strokeWidth={2.5} />
              <div>
                <p className="text-indigo-200 text-[10px] font-black uppercase tracking-widest mb-1">Live Feed Flow</p>
                <h4 className="text-white text-3xl font-black tracking-tighter leading-none">Мониторинг <br /> трафика.</h4>
              </div>
            </div>
            <div className="bg-white/[0.03] border border-white/5 rounded-[32px] p-8 flex flex-col justify-between h-64">
              <Globe className="text-slate-600" size={32} />
              <div>
                <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-1">Device Connectivity</p>
                <h4 className="text-white text-3xl font-black tracking-tighter leading-none">Hikvision <br /> Network.</h4>
              </div>
            </div>
          </div>

          {/* Layer 3: Technical Footer */}
          <div className="flex items-center justify-center gap-10 opacity-40">
            <div className="flex items-center gap-2">
              <Zap size={12} className="text-slate-500" />
              <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Neural ID Engine</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={12} className="text-slate-500" />
              <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Biometric Encrypted</span>
            </div>
          </div>

        </div>
      </div>

    </div>
  )
}